import { useState, useRef, useEffect, useCallback } from 'react';
import { getAccessToken, getRefreshToken, updateAccessToken } from '../services/authStorage';
import { isUncalibratedResponse, markUncalibratedName, buildUncalibratedWarning } from '../utils/calibratedDownload';

const API_BASE_URL = '/api/v1';
const CONCURRENCY_LIMIT = 4;

/**
 * Hook for downloading files with progress tracking and session keep-alive.
 * Consolidates the duplicate download logic from UserFiles.js.
 */
export function useFileDownload() {
  const [downloadProgress, setDownloadProgress] = useState({
    status: 'idle', // idle | fetching | downloading | complete | error
    current: 0,
    total: 0,
    filename: '',
    error: ''
  });

  const sessionKeepAliveInterval = useRef(null);
  const supportsDirectoryPicker = 'showDirectoryPicker' in window;

  const refreshSession = async () => {
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) return;
      const response = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          updateAccessToken(data.data.accessToken, data.data.refreshToken);
        }
      }
    } catch (error) {
      console.warn('[Download] Token refresh error:', error.message);
    }
  };

  const startSessionKeepAlive = () => {
    if (sessionKeepAliveInterval.current) return;
    refreshSession();
    sessionKeepAliveInterval.current = setInterval(refreshSession, 5 * 60 * 1000);
  };

  const stopSessionKeepAlive = () => {
    if (sessionKeepAliveInterval.current) {
      clearInterval(sessionKeepAliveInterval.current);
      sessionKeepAliveInterval.current = null;
    }
  };

  useEffect(() => {
    return () => stopSessionKeepAlive();
  }, []);

  const getDownloadUrl = useCallback((fileId) => {
    const token = getAccessToken();
    return `${API_BASE_URL}/user-files/download/${fileId}?token=${encodeURIComponent(token)}`;
  }, []);

  const getCalibratedDownloadUrl = useCallback((fileId) => {
    const token = getAccessToken();
    return `${API_BASE_URL}/calibration/download/${fileId}?format=calibrated&token=${encodeURIComponent(token)}`;
  }, []);

  /**
   * Download a calibrated file via fetch, handling errors gracefully instead of
   * navigating the browser to a JSON error response.
   * Returns { success: true } or { success: false, error: string }.
   */
  const downloadCalibrated = useCallback(async (fileId) => {
    const token = getAccessToken();
    const url = `${API_BASE_URL}/calibration/download/${fileId}?format=calibrated&token=${encodeURIComponent(token)}`;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        let errorMsg = 'Calibration failed';
        try {
          const json = await response.json();
          if (json.error) errorMsg = json.error;
        } catch { /* not JSON */ }
        return { success: false, error: errorMsg };
      }
      // Success — trigger browser download from the response blob
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `calibrated_${fileId}.fits`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(blobUrl);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message || 'Download failed' };
    }
  }, []);

  const downloadFiles = useCallback(async (fileIds, { format = 'raw' } = {}) => {
    if (!supportsDirectoryPicker) {
      alert(
        !window.isSecureContext
          ? 'Bulk "Download to Folder" requires a secure (HTTPS) connection.\n\n' +
            'Please access this site via HTTPS, or download files individually using the download buttons.'
          : 'Bulk "Download to Folder" is only supported in Chrome, Edge, or another Chromium-based browser.\n\n' +
            'In Firefox or Safari this feature is unavailable — please download files individually, ' +
            'or switch to Chrome/Edge for bulk downloads.'
      );
      return;
    }

    const concurrencyLimit = format === 'calibrated' ? 2 : CONCURRENCY_LIMIT;

    try {
      const dirHandle = await window.showDirectoryPicker({ mode: 'readwrite', startIn: 'downloads' });
      startSessionKeepAlive();
      setDownloadProgress({ status: 'downloading', current: 0, total: fileIds.length, filename: '', error: '' });

      const token = getAccessToken();
      let completedCount = 0;
      // #1276: frames the server couldn't calibrate come back raw (X-Calibration-Status:
      // unavailable) — collect them so we can warn once the batch finishes.
      const uncalibrated = [];

      const downloadFile = async (fileId, index) => {
        const downloadUrl = format === 'calibrated'
          ? `${API_BASE_URL}/calibration/download/${fileId}?format=both&token=${encodeURIComponent(token)}`
          : `${API_BASE_URL}/user-files/download/${fileId}?token=${encodeURIComponent(token)}`;
        try {
          const response = await fetch(downloadUrl);
          if (!response.ok) return false;

          const contentDisposition = response.headers.get('Content-Disposition');
          let filename = `file_${fileId}.fits`;
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
            if (match) filename = match[1];
          }

          // #1276: a `format=both` request returns the RAW frame with
          // X-Calibration-Status: unavailable when it can't be calibrated. Mark it so
          // it's never mistaken for calibrated data, and warn once at the end.
          if (format === 'calibrated'
              && isUncalibratedResponse(response.headers.get('X-Calibration-Status'))) {
            uncalibrated.push(filename);
            filename = markUncalibratedName(filename);
          }

          const blob = await response.blob();
          const fileHandle = await dirHandle.getFileHandle(filename, { create: true });
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          return true;
        } catch (fileErr) {
          console.error(`Error downloading file ${fileId}:`, fileErr);
          return false;
        }
      };

      for (let i = 0; i < fileIds.length; i += concurrencyLimit) {
        const batch = fileIds.slice(i, i + concurrencyLimit);
        setDownloadProgress({
          status: 'downloading', current: completedCount, total: fileIds.length,
          filename: `Downloading batch ${Math.floor(i / concurrencyLimit) + 1}${format === 'calibrated' ? ' (calibrating)' : ''}...`, error: ''
        });

        const results = await Promise.all(batch.map((fileId, batchIndex) => downloadFile(fileId, i + batchIndex)));
        completedCount += results.filter(Boolean).length;

        setDownloadProgress({
          status: 'downloading', current: completedCount, total: fileIds.length,
          filename: `${completedCount} of ${fileIds.length} completed...`, error: ''
        });
      }

      stopSessionKeepAlive();
      setDownloadProgress({ status: 'complete', current: completedCount, total: fileIds.length, filename: '', error: '' });
      const uncalibratedWarning = buildUncalibratedWarning(uncalibrated);
      if (uncalibratedWarning) alert(uncalibratedWarning);
    } catch (err) {
      stopSessionKeepAlive();
      if (err.name === 'AbortError') {
        setDownloadProgress({ status: 'idle', current: 0, total: 0, filename: '', error: '' });
        return;
      }
      setDownloadProgress({ status: 'error', current: 0, total: 0, filename: '', error: err.message || 'Download failed' });
    }
  }, [supportsDirectoryPicker]);

  const downloadGroup = useCallback(async (groupType, groupId) => {
    if (!supportsDirectoryPicker) {
      alert(
        !window.isSecureContext
          ? 'Bulk "Download to Folder" requires a secure (HTTPS) connection.\n\n' +
            'Please access this site via HTTPS, or download files individually using the download buttons.'
          : 'Bulk "Download to Folder" is only supported in Chrome, Edge, or another Chromium-based browser.\n\n' +
            'In Firefox or Safari this feature is unavailable — please download files individually, ' +
            'or switch to Chrome/Edge for bulk downloads.'
      );
      return;
    }

    try {
      const dirHandle = await window.showDirectoryPicker({ mode: 'readwrite', startIn: 'downloads' });
      startSessionKeepAlive();
      setDownloadProgress({ status: 'fetching', current: 0, total: 0, filename: '', error: '' });

      const token = getAccessToken();
      const listResponse = await fetch(
        `${API_BASE_URL}/user-files/group-files?groupType=${groupType}&groupId=${encodeURIComponent(groupId)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!listResponse.ok) throw new Error(`Failed to get file list: ${listResponse.status}`);
      const listResult = await listResponse.json();
      if (!listResult.success) throw new Error(listResult.error?.message || 'Failed to get file list');

      const files = listResult.data.files || [];
      if (files.length === 0) {
        stopSessionKeepAlive();
        alert('No files found in this group');
        setDownloadProgress({ status: 'idle', current: 0, total: 0, filename: '', error: '' });
        return;
      }

      setDownloadProgress({ status: 'downloading', current: 0, total: files.length, filename: '', error: '' });
      let completedCount = 0;

      const downloadFile = async (file, index) => {
        const fileId = file.file_id || file._id;
        const downloadUrl = `${API_BASE_URL}/user-files/download/${fileId}?token=${encodeURIComponent(token)}`;
        let filename = file.original_filename || file.filename || file.file_name || `file_${fileId}.fits`;

        try {
          const response = await fetch(downloadUrl);
          if (!response.ok) return false;

          const contentDisposition = response.headers.get('Content-Disposition');
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
            if (match) filename = match[1];
          }

          const blob = await response.blob();
          const fileHandle = await dirHandle.getFileHandle(filename, { create: true });
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          return true;
        } catch (fileErr) {
          console.error(`Error downloading file ${filename}:`, fileErr);
          return false;
        }
      };

      for (let i = 0; i < files.length; i += CONCURRENCY_LIMIT) {
        const batch = files.slice(i, i + CONCURRENCY_LIMIT);
        const batchNames = batch.map(f => (f.original_filename || f.filename || '').split('.')[0]).join(', ');

        setDownloadProgress({
          status: 'downloading', current: completedCount, total: files.length,
          filename: `Downloading: ${batchNames}...`, error: ''
        });

        const results = await Promise.all(batch.map((file, batchIndex) => downloadFile(file, i + batchIndex)));
        completedCount += results.filter(Boolean).length;

        setDownloadProgress({
          status: 'downloading', current: completedCount, total: files.length,
          filename: `${completedCount} of ${files.length} completed...`, error: ''
        });
      }

      stopSessionKeepAlive();
      setDownloadProgress({ status: 'complete', current: completedCount, total: files.length, filename: '', error: '' });
    } catch (err) {
      stopSessionKeepAlive();
      if (err.name === 'AbortError') {
        setDownloadProgress({ status: 'idle', current: 0, total: 0, filename: '', error: '' });
        return;
      }
      setDownloadProgress({ status: 'error', current: 0, total: 0, filename: '', error: err.message || 'Download failed' });
    }
  }, [supportsDirectoryPicker]);

  const cancelDownload = useCallback(() => {
    setDownloadProgress({ status: 'idle', current: 0, total: 0, filename: '', error: '' });
  }, []);

  return { downloadFiles, downloadGroup, downloadProgress, cancelDownload, getDownloadUrl, getCalibratedDownloadUrl, downloadCalibrated };
}
